import { useEffect, useRef, useState } from "react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";
import { Input } from "@/components/ui/input";

const icon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  shadowSize: [41, 41],
});

const Recenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.setView(position, map.getZoom() < 15 ? 16 : map.getZoom());
  }, [position, map]);
  return null;
};

/** Draggable pin the owner can nudge until it sits exactly on their premise. */
export const LocationPicker = ({ address, postcode, district, state, value, onChange }) => {
  const t = useT();
  const [point, setPoint] = useState(value || null);
  const [status, setStatus] = useState("idle");
  const [term, setTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const moved = useRef(false);

  const searchPlace = async () => {
    const q = term.trim();
    if (q.length < 3) {
      toast.error(t("map.toast.short"));
      return;
    }
    setSearching(true);
    const attempts = [
      [q, district, state, "Malaysia"].filter(Boolean).join(", "),
      [q, state, "Malaysia"].filter(Boolean).join(", "),
      `${q}, Malaysia`,
    ];
    try {
      let found = null;
      for (const attempt of attempts) {
        try {
          const { data } = await api.get("/geocode", { params: { q: attempt } });
          found = data;
          break;
        } catch {
          // cuba bentuk carian seterusnya
        }
      }
      if (!found) {
        toast.error(t("map.toast.notFound"));
        return;
      }
      moved.current = true;
      const next = { lat: found.lat, lng: found.lon };
      setPoint(next);
      onChange?.(next);
      setStatus("ready");
      toast.success(t("map.toast.found"));
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (moved.current || !district || !state) return;
    let cancelled = false;
    setStatus("loading");
    api
      .get("/geocode/place", { params: { state, district, postcode: postcode || "", address: address || "" } })
      .then(({ data }) => {
        if (cancelled) return;
        const next = { lat: data.lat, lng: data.lon };
        setPoint(next);
        onChange?.(next);
        setStatus("ready");
      })
      .catch(() => !cancelled && setStatus("failed"));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, postcode, district, state]);

  const position = point ? [point.lat, point.lng] : null;

  if (!district || !state) {
    return (
      <p data-testid="location-picker-hint" className="mt-3 rounded-lg border border-dashed border-border p-5 text-sm text-slate-500">
        {t("map.hint")}
      </p>
    );
  }

  return (
    <div className="mt-3" data-testid="location-picker">
      <div className="mb-3 flex gap-2">
        <Input
          data-testid="location-search-input"
          placeholder={t("map.search")}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              searchPlace();
            }
          }}
          className="h-11"
        />
        <Button
          type="button"
          variant="outline"
          data-testid="location-search-button"
          disabled={searching}
          onClick={searchPlace}
          className="h-11 shrink-0"
        >
          {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          <span className="ml-2 hidden sm:inline">{t("map.searchBtn")}</span>
        </Button>
      </div>
      <div className="overflow-hidden rounded-lg border border-border bg-slate-100">
        {status === "loading" && !position && (
          <div className="grid h-64 place-items-center text-sm text-slate-500">
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("map.searching")}
            </span>
          </div>
        )}
        {status === "failed" && !position && (
          <div data-testid="location-picker-failed" className="grid h-64 place-items-center px-6 text-center text-sm text-slate-500">
            {t("map.failed")}
          </div>
        )}
        {position && (
          <MapContainer center={position} zoom={16} scrollWheelZoom={false} style={{ height: "16rem", width: "100%" }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Recenter position={position} />
            <Marker
              position={position}
              icon={icon}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  moved.current = true;
                  const next = { lat, lng };
                  setPoint(next);
                  onChange?.(next);
                },
              }}
            />
          </MapContainer>
        )}
      </div>
      <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-500">
        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t("map.adjust")}
        {point && (
          <span data-testid="location-picker-coords" className="ml-1 font-semibold text-slate-600">
            ({point.lat.toFixed(5)}, {point.lng.toFixed(5)})
          </span>
        )}
      </p>
    </div>
  );
};
